import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Building2, Lock, Loader2, Mail, KeyRound, ShieldCheck, ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff
} from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import {
  requestVendorPasswordReset,
  resetVendorPassword,
  validateVendorPassword,
} from '../services/vendorAuth'
import '../App.css'

type ForgotStep = 'email' | 'reset'

const VendorForgotPassword = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isBooking = location.pathname.startsWith('/booking')
  const loginPath = isBooking ? '/booking/login' : '/vendor/login'

  const [forgotStep, setForgotStep] = useState<ForgotStep>('email')
  const [forgotEmail, setForgotEmail] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resetComplete, setResetComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!forgotEmail.trim()) {
      setError('Please enter your registered email address.')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await requestVendorPasswordReset(forgotEmail)
      setForgotStep('reset')
      setSuccess('A temporary reset code has been sent to your email. Enter it below with your new password.')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send reset code.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!resetCode.trim()) {
      setError('Please enter the temporary code from your email.')
      return
    }

    const validationError = validateVendorPassword(newPassword)
    if (validationError) {
      setPasswordError(validationError)
      setError(validationError)
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Password confirmation does not match.')
      return
    }

    setLoading(true)

    try {
      await resetVendorPassword(forgotEmail, resetCode, newPassword, confirmPassword)
      setResetComplete(true)
      setSuccess('Password updated successfully. Redirecting to login...')
      window.setTimeout(() => {
        navigate(loginPath, { replace: true, state: { passwordReset: true } })
      }, 1600)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset password.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value)
    setPasswordError(value ? validateVendorPassword(value) : null)
    if (error) setError(null)
  }

  const handleBackToEmailStep = () => {
    setForgotStep('email')
    setResetCode('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError(null)
    setError(null)
    setSuccess(null)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-500/20 mb-4">
            <Building2 className="h-8 w-8 text-primary-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Forgot Password</h1>
          <p className="text-gray-300 max-w-xl mx-auto">
            Reset your host account password using a temporary code sent to your email.
          </p>
        </div>
      </section>

      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              {success}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-primary-500 px-8 py-5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Reset Password
              </h2>
            </div>

            <div className="p-8">
              {forgotStep === 'email' ? (
                <>
                  <p className="text-sm text-gray-600 mb-6">
                    Enter your registered email and we&apos;ll send a temporary code to reset your password.
                  </p>
                  <form onSubmit={handleSendResetCode} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary-500" />
                          Email <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="Your registered email"
                        required
                        disabled={loading}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || resetComplete}
                      className="w-full bg-gray-800 text-white py-3.5 rounded-xl font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending code...
                        </>
                      ) : (
                        <>
                          Send Reset Code
                          <ArrowRight className="h-5 w-5" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-6">
                    Enter the temporary code sent to <strong>{forgotEmail}</strong> and choose a new password.
                  </p>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-primary-500" />
                          Temporary code <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <input
                        type="text"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                        placeholder="Code from email"
                        required
                        disabled={loading}
                        maxLength={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase tracking-widest"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-primary-500" />
                          New password <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => handleNewPasswordChange(e.target.value)}
                          placeholder="New password"
                          required
                          disabled={loading}
                          className={`w-full px-4 py-3 pr-11 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            passwordError ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(prev => !prev)}
                          disabled={loading}
                          aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-primary-500" />
                          Confirm password <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          required
                          disabled={loading}
                          className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(prev => !prev)}
                          disabled={loading}
                          aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500">
                      Password must be 8–32 characters, alphanumeric only, with at least one letter and one number.
                    </p>

                    {passwordError && (
                      <p className="text-xs text-red-600">{passwordError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading || resetComplete}
                      className="w-full bg-primary-500 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Updating password...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleBackToEmailStep}
                      disabled={loading}
                      className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
                    >
                      ← Use a different email
                    </button>
                  </form>
                </>
              )}

              <div className="mt-6 pt-6 border-t border-gray-100">
                <Link
                  to={loginPath}
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {resetComplete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in-overlay">
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full mx-4 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center animate-success-pop">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Password reset!</h3>
                <p className="text-sm text-gray-600 mt-1">Redirecting to host login...</p>
              </div>
              <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorForgotPassword
